import type { AnalyticsEventProperties, AnalyticsProvider, AnalyticsUserTraits } from "./types";

/**
 * Fans every call out to every registered provider. This is the only thing the rest of the app
 * talks to — a call site says `analytics.track(...)` and never knows or cares whether that
 * means GA4 alone, GA4 plus a second provider, or nothing at all yet.
 *
 * Nothing reaches a provider until `setEnabled(true)`, which is the consent gate: providers are
 * lazily `init()`-ed the first time consent is granted, and never before, so no tracking script
 * and no cookie is set until the visitor has actually agreed to it. See lib/consent.tsx.
 */
export class AnalyticsManager {
  private readonly providers: AnalyticsProvider[] = [];
  private enabled = false;
  private initialised = false;

  /** Adds a provider to the fan-out list. Safe to call before or after consent is granted —
   *  a provider registered late is initialised immediately if consent was already given. */
  register(provider: AnalyticsProvider): void {
    this.providers.push(provider);
    if (this.enabled) this.initialiseOne(provider);
  }

  /** The consent gate. Flipping this to `true` for the first time initialises every registered
   *  provider; flipping it back to `false` simply stops forwarding further calls — vendor
   *  scripts already loaded can't be un-loaded, but nothing more is sent to them. */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (enabled && !this.initialised) {
      this.initialised = true;
      for (const provider of this.providers) this.initialiseOne(provider);
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  page(path: string, title?: string): void {
    if (!this.enabled) return;
    this.forEach((provider) => provider.page(path, title));
  }

  track(event: string, properties?: AnalyticsEventProperties): void {
    if (!this.enabled) return;
    this.forEach((provider) => provider.track(event, properties));
  }

  identify(userId: string, traits?: AnalyticsUserTraits): void {
    if (!this.enabled) return;
    this.forEach((provider) => provider.identify(userId, traits));
  }

  reset(): void {
    if (!this.enabled) return;
    this.forEach((provider) => provider.reset());
  }

  private initialiseOne(provider: AnalyticsProvider): void {
    this.safely(provider, () => provider.init());
  }

  private forEach(fn: (provider: AnalyticsProvider) => void): void {
    for (const provider of this.providers) this.safely(provider, () => fn(provider));
  }

  // A single misbehaving provider — a blocked script, a vendor SDK throwing on a bad payload —
  // must never take the others down with it, and must never break the page it's measuring.
  private safely(provider: AnalyticsProvider, fn: () => void): void {
    try {
      fn();
    } catch (error) {
      console.error(`[analytics] ${provider.name} failed`, error);
    }
  }
}
